// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-10-14) — reference lines: a boundary that gives a
 * dataset's values their meaning, such as the Paris Agreement's 1.5 °C on
 * the temperature projections or Ω = 1 on aragonite saturation, below
 * which seawater dissolves shells.
 *
 * Each line is drawn dashed on the pin graph (browser panel, its window
 * over the globe, the headset's Data page) and marked on the dataset's
 * legend (browser colour bar, phone chip, headset legend). One switch,
 * saved per browser, shows or hides them all: Tools → View, the line's
 * own key under the graph, or the headset's View page.
 *
 * A line is only declared where it holds against the dataset's own
 * baseline: the Paris lines are warming since 1850–1900, so they go on the
 * four CMIP6 projections measured from 1850–1900, and not on NOAA's
 * anomaly (1901–2000) or a change from a held baseline (the caller passes
 * no lines while one is shown). The Paris limits apply to the global
 * average, which the line's note says, since one place can pass 1.5 °C
 * long before the world does.
 *
 * Keyed by the app's dataset id (`R2_DASH_<dsa id>`), which a publisher's
 * renaming leaves alone.
 */

import { t, type MessageKey } from '../i18n'
import { formatNumber } from '../i18n/format'

/** A boundary on a dataset's value axis, in the dataset's own units. */
export interface ReferenceLine {
  /** Stable id, for keys and tests. */
  readonly id: 'paris15' | 'paris2' | 'aragonite1'
  readonly value: number
}

const PARIS: readonly ReferenceLine[] = [
  { id: 'paris15', value: 1.5 },
  { id: 'paris2', value: 2 },
]
const ARAGONITE: readonly ReferenceLine[] = [{ id: 'aragonite1', value: 1 }]

/** The lines per dataset. */
const LINES: Readonly<Record<string, readonly ReferenceLine[]>> = {
  // Projected Surface Temperature Change (CMIP6), relative to 1850–1900.
  R2_DASH_e1b3727e39f60146: PARIS, // cmip6_tas_anomaly_ssp126
  R2_DASH_232aac3472d889eb: PARIS, // cmip6_tas_anomaly_ssp245
  R2_DASH_054550e4e88b3952: PARIS, // cmip6_tas_anomaly_ssp370
  R2_DASH_7a7303ae451bcf27: PARIS, // cmip6_tas_anomaly_ssp585
  // Projected Aragonite Saturation (CMIP6), Ωarag.
  R2_DASH_0902c40cde214753: ARAGONITE, // cmip6_aragonite_saturation_ssp126
  R2_DASH_a2d7d64172fc5696: ARAGONITE, // cmip6_aragonite_saturation_ssp245
  R2_DASH_d74b739da7e3fdd5: ARAGONITE, // cmip6_aragonite_saturation_ssp370
  R2_DASH_52d11fde93a51c55: ARAGONITE, // cmip6_aragonite_saturation_ssp585
}

/** The dataset's reference lines, whether shown or not; none for an unknown id. */
export function referenceLinesOf(datasetId: string | null | undefined): readonly ReferenceLine[] {
  return (datasetId && LINES[datasetId]) || []
}

/** The line's name in full: "1.5 °C: Paris Agreement goal (global average)". */
export function referenceLabel(line: ReferenceLine): string {
  return t(`reference.${line.id}.label` as MessageKey)
}

/** Short enough for a legend's axis: "Paris 1.5". */
export function referenceShort(line: ReferenceLine): string {
  return t(`reference.${line.id}.short` as MessageKey)
}

/** What the line means and where it holds, for a tooltip. */
export function referenceNote(line: ReferenceLine): string {
  return t(line.id === 'aragonite1' ? 'reference.aragonite1.note' : 'reference.paris.note')
}

/** The value as an axis prints it. */
export function referenceValueText(line: ReferenceLine): string {
  return formatNumber(line.value, { maximumSignificantDigits: 3 })
}

// --- the switch ---------------------------------------------------------

const STORAGE_KEY = 'ayni-reference-lines'

let visible: boolean | null = null
const listeners = new Set<() => void>()

/** Shown unless the viewer hid them (the default favours the context). */
export function referenceLinesVisible(): boolean {
  if (visible === null) {
    try {
      visible = localStorage.getItem(STORAGE_KEY) !== 'off'
    } catch {
      visible = true
    }
  }
  return visible
}

export function setReferenceLinesVisible(on: boolean): void {
  if (referenceLinesVisible() === on) return
  visible = on
  try {
    localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off')
  } catch {
    // A private window: the choice lasts this visit.
  }
  for (const fn of listeners) fn()
}

/** Called after the switch changes; returns the unsubscribe. */
export function onReferenceLinesChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Tests: forget the cached switch. */
export function resetReferenceLinesForTest(): void {
  visible = null
  listeners.clear()
}

/** The lines to draw for a dataset now: none while they are hidden. */
export function shownReferenceLines(datasetId: string | null | undefined): readonly ReferenceLine[] {
  return referenceLinesVisible() ? referenceLinesOf(datasetId) : []
}
